/**
 * PlansPage — Platform-wide plan directory with modern clean design.
 */
import { useState, useMemo, useEffect, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { confirmDialog } from 'primereact/confirmdialog'
import { Column } from 'primereact/column'
import {
  Box, Users, Star, Eye, Pencil, Trash2, Plus, Search, RefreshCw,
} from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import { PlanDrawer, type PlanDrawerMode } from '../../components/admin/platform/PlanDrawer.js'
import DataTable from '../../components/ui/DataTable.js'
import { usePlans, usePlanFeatures, type Plan, type UsePlansFilters } from '../../hooks/usePlans.js'
import { useToast } from '../../components/system/ToastProvider.js'
import type {} from '../../api/client.js'

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Box
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div
          className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: `${accent}10` }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: accent }}
      />
    </div>
  )
}

/* ─── Action Icon ─── */
function ActionIcon({ icon: Icon, label, onClick, color }: {
  icon: typeof Eye
  label: string
  onClick: () => void
  color: string
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 transition-all duration-150 hover:border-transparent hover:text-white"
      style={{ ['--color' as any]: color }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = (e.currentTarget as HTMLElement).style.getPropertyValue('--color')
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'
        ;(e.currentTarget as HTMLElement).style.borderColor = '#f3f4f6'
      }}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

export default function PlansPage(): JSX.Element {
  return (
    <RequirePermission permission="platform:tenants_manage">
      <PlansPageInner />
    </RequirePermission>
  )
}

function PlansPageInner(): JSX.Element {
  const toast = useToast()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const filters = useMemo<UsePlansFilters>(() => {
    return debouncedSearch ? { search: debouncedSearch } : {}
  }, [debouncedSearch])

  const { items, loading, refetch, remove } = usePlans(filters)
  const { items: features } = usePlanFeatures()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Plan | null>(null)
  const [drawerMode, setDrawerMode] = useState<PlanDrawerMode>('create')

  function handleDelete(plan: Plan): void {
    confirmDialog({
      message: `Delete plan "${plan.name}"? This cannot be undone.`,
      header: `Delete ${plan.name}`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await remove(plan.id)
          toast.success(`${plan.name} deleted`)
          await refetch()
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Failed to delete plan.')
        }
      },
    })
  }

  const totalTenants = items.reduce((acc, p) => acc + (p.tenantCount || 0), 0)

  return (
    <div className="w-full space-y-6 px-4 pb-10">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Plans</h1>
          <p className="text-sm text-gray-400 mt-1">Tiered service plans assignable to tenants.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/platform/plan-features')}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
          >
            Manage Features
          </button>
          <button
            onClick={() => { setEditing(null); setDrawerMode('create'); setDrawerOpen(true) }}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
          >
            <Plus className="h-4 w-4" />
            New Plan
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Total Plans" value={items.length} icon={Box} accent="#3454cf" />
        <StatCard label="Active Tenants" value={totalTenants} icon={Users} accent="#10b981" />
        <StatCard label="Custom Plans" value={items.length > 5 ? items.length - 5 : 0} icon={Star} accent="#8b5cf6" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
          <input
            type="text"
            placeholder="Search plans by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 focus:outline-none transition-all"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 font-medium">{items.length} plans</span>
          <div className="w-px h-4 bg-gray-200" />
          <button
            onClick={() => { setSearch(''); void refetch() }}
            className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        value={items}
        loading={loading}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
        emptyMessage="No plans found."
      >
        <Column
          field="name"
          header="Plan Name"
          sortable
          body={(plan: Plan) => {
            const shortId = plan.id.split('-')[0]?.substring(0, 4) || '??'
            return (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                  <Box className="h-5 w-5 text-[#3454cf]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{plan.name}</p>
                  <p className="text-xs text-gray-400">ID: {shortId}</p>
                </div>
              </div>
            )
          }}
          style={{ width: '25%' }}
        />
        <Column
          field="maxAgents"
          header="Max Agents"
          sortable
          body={(plan: Plan) => (
            <span className="inline-flex items-center rounded-sm bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-600">
              {plan.maxAgents} Agents
            </span>
          )}
        />
        <Column
          field="maxConcurrentCalls"
          header="Max Calls"
          sortable
          body={(plan: Plan) => (
            <span className="inline-flex items-center rounded-sm bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-600">
              {plan.maxConcurrentCalls} Calls
            </span>
          )}
        />
        <Column
          header="Features"
          body={(plan: Plan) => {
            const featuresObj = (plan.features as any) || {}
            const count = Object.keys(featuresObj).filter(k => featuresObj[k]).length
            return (
              <span className="inline-flex items-center rounded-sm bg-[#3454cf]/5 px-2.5 py-1 text-xs font-semibold text-[#3454cf]">
                {count} Features
              </span>
            )
          }}
        />
        <Column
          field="tenantCount"
          header="Tenants"
          sortable
          body={(plan: Plan) => (
            <span className="inline-flex items-center rounded-sm bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600">
              {plan.tenantCount} Tenants
            </span>
          )}
        />
        <Column
          header="Actions"
          body={(plan: Plan) => (
            <div className="flex items-center gap-1">
              <ActionIcon icon={Eye} label="View" onClick={() => { setEditing(plan); setDrawerMode('view'); setDrawerOpen(true) }} color="#3454cf" />
              <ActionIcon icon={Pencil} label="Edit" onClick={() => { setEditing(plan); setDrawerMode('edit'); setDrawerOpen(true) }} color="#3454cf" />
              <ActionIcon icon={Trash2} label="Delete" onClick={() => handleDelete(plan)} color="#ef4444" />
            </div>
          )}
          style={{ width: '120px' }}
        />
      </DataTable>

      <PlanDrawer
        open={drawerOpen}
        existing={editing}
        features={features}
        mode={drawerMode}
        onClose={() => {
          setDrawerOpen(false)
          setEditing(null)
          setDrawerMode('create')
        }}
        onSaved={() => {
          setDrawerOpen(false)
          setEditing(null)
          setDrawerMode('create')
          void refetch()
        }}
      />
    </div>
  )
}
